import { Lesson, curve, ellipse, type LessonLevel, type XY, K, R, Y, G, B, W } from './drawing-lesson-brushes';
import { independentLesson } from './drawing-independent';
import { ANIMAL_EXAMPLES } from './drawing-lessons-animals';
import { NATURE_EXAMPLES } from './drawing-lessons-nature';
import { OBJECT_EXAMPLES } from './drawing-lessons-objects';
import { LIGHTHOUSE_EXAMPLES } from './drawing-lessons-lighthouse';
import { WILDLIFE_EXAMPLES } from './drawing-lessons-wildlife';
import { BOTANICAL_EXAMPLES } from './drawing-lessons-botanical';
import { SCENE_EXAMPLES } from './drawing-lessons-scenes';
import { ORIGINAL_EXAMPLE_WORD_IDS } from './drawing-example-word-links';
import type { DrawingExample } from './drawing-lesson-brushes';
import { SKY_EXAMPLES } from './drawing-lessons-sky';
import { FRUIT_EXAMPLES } from './drawing-lessons-fruit';
import { MEADOW_EXAMPLES } from './drawing-lessons-meadow';
import { STILL_LIFE_EXAMPLES } from './drawing-lessons-still-life';
import { SCIENCE_EXAMPLES } from './drawing-lessons-science';
import { PEOPLE_EXAMPLES } from './drawing-lessons-people';
import { DAILY_LIFE_EXAMPLES } from './drawing-lessons-daily-life';
import { WEATHER_EXAMPLES } from './drawing-lessons-weather';
import { MUSIC_EXAMPLES } from './drawing-lessons-music';
import { ACTION_EXAMPLES } from './drawing-lessons-actions';
import { FABLE_EXAMPLES } from './drawing-lessons-fables';
import { OUTING_EXAMPLES } from './drawing-lessons-outings';
import { GAME_PROP_EXAMPLES } from './drawing-lessons-game-props';
import { SPORTS_EXAMPLES } from './drawing-lessons-sports';
import { FESTIVAL_EXAMPLES } from './drawing-lessons-festivals';
import { IDIOM_EXAMPLES } from './drawing-lessons-idioms';
export { LESSON_LEVELS, type LessonLevel, type DrawingLesson } from './drawing-lesson-brushes';

// 首批十个题材的入门版：清楚的大形、免费六色、四步讲完。
// 进阶与艺术版由 drawing-independent.ts 独立构图，不复用这里的入门线稿。
// 新题材按主题分模块登记；所有级别按需生成，避免微信启动时构造全部笔迹。
function cat(level: LessonLevel) {
  if (level !== 0) return independentLesson('cat', level);
  const d = new Lesson('cat', level);
  d.step('大形先行', '先画椭圆身体，再给尾巴留出弯弯的位置。');
  d.line(curve([651, 739], [[891, 769], [851, 504], [763, 582]]), Y, 65);
  d.shape(ellipse(495, 674, 155, 157), Y);
  d.step('尖耳圆脸', '耳朵像两个小三角，脸颊用圆弧连起来。');
  const head = curve([292, 409], [[281, 362], [284, 232], [312, 221]], [[331, 225], [390, 278], [411, 293]], [[478, 275], [537, 279], [584, 295]], [[620, 255], [673, 218], [691, 223]], [[718, 267], [707, 365], [701, 416]], [[735, 566], [624, 607], [498, 604]], [[370, 609], [255, 556], [292, 409]]);
  d.shape(head, Y);
  d.shape([[311, 263], [368, 307], [315, 335]], R, R); d.shape([[672, 262], [619, 305], [678, 336]], R, R);
  d.step('画出小表情', '眼睛放在同一高度，鼻子下接两个小弯钩。');
  d.dot(408, 432, 23).dot(592, 432, 23).dot(501, 468, 20, R);
  d.line(curve([501, 480], [[498, 516], [474, 519], [457, 501]]), K, 7); d.line(curve([501, 480], [[504, 516], [528, 519], [544, 501]]), K, 7);
  [[326, 452, 269, 436], [328, 480, 267, 488], [671, 452, 732, 436], [674, 480, 736, 489]].forEach(([x, y, a, b]) => d.line([[x, y], [a, b]], K, 6));
  d.step('花纹和小爪', '用短线画额头花纹，最后加上项圈和小爪子。');
  d.line([[467, 300], [473, 346]], R, 17).line([[502, 293], [502, 343]], R, 17).line([[537, 300], [531, 346]], R, 17);
  d.line([[385, 610], [493, 637], [613, 610]], R, 22).dot(497, 646, 28, Y);
  d.line([[446, 700], [437, 794], [410, 797]], K, 7).line([[544, 700], [553, 794], [580, 797]], K, 7);
  d.star(210, 321, 26); d.star(795, 410, 22);
  return d.done('cat', '小猫', '圆形组合 · 表情', ['猫', '动物', '宠物', '可爱', '脸部']);
}
function panda(level: LessonLevel) {
  if (level !== 0) return independentLesson('panda', level);
  const d = new Lesson('panda', level);
  d.step('先画黑白大形', '圆耳朵、椭圆身体，先确定胖乎乎的比例。');
  d.shape(ellipse(347, 285, 75, 78), K); d.shape(ellipse(648, 285, 75, 78), K);
  d.shape(ellipse(500, 674, 157, 171), W);
  d.shape(ellipse(365, 684, 55, 109), K); d.shape(ellipse(635, 684, 55, 109), K);
  d.shape(ellipse(402, 801, 80, 49), K); d.shape(ellipse(596, 801, 80, 49), K);
  d.step('圆脸与眼圈', '白色圆脸盖在身体前面，左右眼圈一样大。');
  d.shape(ellipse(500, 440, 206, 182), W);
  d.shape(ellipse(414, 431, 49, 65), K); d.shape(ellipse(587, 431, 49, 65), K);
  d.step('留出眼睛高光', '黑色里点上白色，眼睛马上就有神了。');
  d.dot(428, 420, 18, W).dot(573, 420, 18, W).dot(501, 489, 24);
  d.line(curve([473, 532], [[491, 552], [516, 552], [534, 532]]), K, 7);
  d.line([[361, 505], [389, 510]], R, 12).line([[610, 510], [638, 505]], R, 12);
  d.step('加一枝竹子', '竹节用短横线，竹叶从枝干向两侧展开。');
  d.line([[743, 823], [755, 333]], G, 23);
  [425, 540, 653, 770].forEach(y => d.line([[739, y], [768, y]], K, 5));
  d.leaf(754, 475, 110, -93); d.leaf(751, 574, -87, -74); d.leaf(749, 697, 104, -63);
  return d.done('panda', '熊猫', '黑白关系 · 遮挡', ['大熊猫', '动物', '竹子', '黑白', '国宝']);
}
function sunflower(level: LessonLevel) {
  if (level !== 0) return independentLesson('sunflower', level);
  const d = new Lesson('sunflower', level);
  d.step('弯弯的茎', '茎从花盆里长出来，带一点弯度才自然。');
  d.line(curve([500, 812], [[470, 690], [524, 560], [502, 428]]), G, 24);
  d.leaf(494, 700, -206, -110); d.leaf(508, 622, 198, -102);
  d.step('花瓣围两圈', '外圈花瓣长、内圈短，错开排列才饱满。');
  for (let i = 0; i < 12; i++) {
    const a = i * Math.PI / 6, dx = Math.cos(a), dy = Math.sin(a), x = 500 + dx * 168, y = 352 + dy * 168;
    d.shape(ellipse(0, 0, 108, 42).map(([u, v]): XY => [x + u * dx - v * dy, y + u * dy + v * dx]), Y);
  }
  for (let i = 0; i < 8; i++) {
    const a = i * Math.PI / 4 + Math.PI / 8, dx = Math.cos(a), dy = Math.sin(a), x = 500 + dx * 120, y = 352 + dy * 120;
    d.shape(ellipse(0, 0, 72, 36).map(([u, v]): XY => [x + u * dx - v * dy, y + u * dy + v * dx]), Y);
  }
  d.step('花心和种子', '深色花心压住花瓣根部，种子一圈圈点出来。');
  d.shape(ellipse(500, 352, 92, 88), K);
  for (let i = 0; i < 14; i++) { const a = i * 2.399, r = Math.sqrt(i) * 20; d.dot(500 + Math.cos(a) * r, 352 + Math.sin(a) * r, 8, Y); }
  d.step('花盆和装饰', '花盆上宽下窄，盆沿加一条横杠和小圆点。');
  d.shape([[392, 762], [608, 762], [578, 896], [422, 896]], R);
  d.shape([[376, 742], [624, 742], [624, 784], [376, 784]], Y);
  [452, 500, 548].forEach(x => d.dot(x, 840, 15, W));
  return d.done('sunflower', '向日葵', '重复花瓣 · 排列', ['花', '植物', '花朵', '黄色', '盆栽']);
}
function strawberry(level: LessonLevel) {
  if (level !== 0) return independentLesson('strawberry', level);
  const d = new Lesson('strawberry', level);
  d.step('倒水滴轮廓', '上宽下尖，但底部也要圆润，别画成三角形。');
  const body = curve([500, 320], [[790, 240], [838, 500], [588, 764]], [[528, 838], [470, 838], [412, 764]], [[164, 500], [210, 240], [500, 320]]);
  d.shape(body, R);
  d.step('叶冠和果柄', '五片尖叶朝四周散开，果柄短短的朝上。');
  d.shape([[500, 290], [430, 232], [444, 300], [324, 288], [404, 372], [378, 404], [490, 356], [536, 424], [572, 350], [672, 372], [592, 296], [576, 234], [518, 292]], G);
  d.line(curve([502, 296], [[494, 240], [522, 208], [552, 202]]), G, 20);
  d.step('种子错开排', '种子沿果肉排开，越靠底部，数量越少。');
  [[360, 448], [476, 438], [596, 452], [684, 436], [404, 548], [528, 556], [628, 560], [460, 652], [572, 656], [516, 744]].forEach(([x, y]) => d.line([[x, y], [x - 7, y + 19]], Y, 12));
  d.step('一抹高光', '在同一侧留出两道白色弧线，果实更饱满。');
  d.line(curve([326, 430], [[300, 460], [316, 512], [332, 532]]), W, 18); d.dot(350, 562, 14, W);
  d.star(798, 690, 26); d.star(206, 268, 22);
  return d.done('strawberry', '草莓', '水滴轮廓 · 高光', ['水果', '食物', '红色', '甜点', '果实']);
}
function icecream(level: LessonLevel) {
  if (level !== 0) return independentLesson('icecream', level);
  const d = new Lesson('icecream', level);
  d.step('先画蛋筒', '倒三角别太宽，尖尖的底部对准画面中心。');
  d.shape([[339, 490], [661, 490], [500, 862]], Y);
  d.step('交叉格纹', '两组斜线交叉，线条停在蛋筒边缘里面。');
  [[377, 552, 567, 686], [402, 612, 541, 741], [430, 672, 516, 797], [623, 552, 433, 686], [598, 612, 459, 741], [570, 672, 484, 797]].forEach(([x, y, a, b]) => d.line([[x, y], [a, b]], R, 6));
  d.step('奶油像一朵云', '几段大圆弧连起来，下面再画一圈软软的裙边。');
  const scoop = curve([303, 483], [[246, 417], [301, 342], [355, 349]], [[341, 223], [460, 196], [508, 261]], [[599, 198], [699, 274], [662, 364]], [[768, 361], [774, 477], [696, 491]], [[645, 544], [598, 511], [567, 498]], [[526, 554], [461, 539], [434, 501]], [[390, 549], [329, 537], [303, 483]]);
  d.shape(scoop, W); d.line(curve([353, 367], [[364, 298], [418, 279], [454, 300]]), R, 24);
  d.line(curve([555, 319], [[605, 291], [637, 325], [628, 350]]), Y, 22);
  d.step('樱桃和糖针', '顶部点一颗樱桃，短短的彩线就是糖针。');
  d.shape(ellipse(506, 226, 43, 42), R); d.line(curve([507, 187], [[506, 151], [536, 133], [552, 145]]), G, 9); d.dot(493, 214, 12, W);
  [[334, 436], [460, 378], [558, 453], [681, 425]].forEach(([x, y], i) => d.line([[x, y], [x + 15, y - 17]], i % 2 ? B : G, 9));
  return d.done('icecream', '冰淇淋', '云朵曲线 · 格纹', ['冰激凌', '食物', '甜点', '奶油', '夏天']);
}
function house(level: LessonLevel) {
  if (level !== 0) return independentLesson('house', level);
  const d = new Lesson('house', level);
  d.step('方块和三角', '墙面用方形，屋顶用三角形，烟囱先贴在屋顶后面。');
  d.shape([[300, 440], [700, 440], [700, 800], [300, 800]], Y);
  d.shape([[640, 300], [700, 300], [700, 430], [640, 430]], R);
  d.shape([[240, 450], [500, 220], [760, 450]], R);
  d.step('门窗找对齐', '窗框横竖交叉，门的下边与地面齐平。');
  d.shape([[360, 500], [464, 500], [464, 610], [360, 610]], B);
  d.line([[412, 506], [412, 604]], W, 8).line([[366, 555], [458, 555]], W, 8);
  d.shape([[540, 590], [648, 590], [648, 798], [540, 798]], W); d.dot(622, 700, 13, R);
  d.step('阁楼圆窗', '屋顶里藏一扇圆窗，再压一条檐口线。');
  d.shape(ellipse(500, 360, 42, 42), W); d.line([[464, 360], [536, 360]], K, 6).line([[500, 324], [500, 396]], K, 6);
  d.line([[262, 460], [738, 460]], K, 8);
  d.step('太阳和花园', '太阳挂在左上角，门前一条小路，两边点些花草。');
  d.shape(ellipse(162, 152, 60, 60), Y);
  for (let i = 0; i < 8; i++) { const a = i * Math.PI / 4 + 0.4; d.line([[162 + Math.cos(a) * 80, 152 + Math.sin(a) * 80], [162 + Math.cos(a) * 106, 152 + Math.sin(a) * 106]], Y, 10); }
  d.line([[560, 812], [520, 912]], Y, 12).line([[640, 812], [692, 912]], Y, 12);
  d.line([[180, 814], [820, 814]], G, 12);
  [240, 760].forEach((x, i) => { d.line([[x, 810], [x, 712]], G, 9); d.leaf(x, 784, i ? 50 : -50, -38); [0, 1, 2, 3, 4].forEach(j => d.dot(x + Math.cos(j * Math.PI * 2 / 5) * 22, 702 + Math.sin(j * Math.PI * 2 / 5) * 22, 26, R)); d.dot(x, 702, 22, Y); });
  return d.done('house', '小房子', '几何组合 · 远近', ['房子', '建筑', '家', '风景', '乡村']);
}
function boat(level: LessonLevel) {
  if (level !== 0) return independentLesson('boat', level);
  const d = new Lesson('boat', level);
  d.step('船身稳稳放', '先画两头翘起的船身，下边用平缓的弧线。');
  d.shape(curve([212, 644], [[386, 670], [636, 670], [806, 644]], [[750, 744], [704, 772], [640, 780]], [[496, 795], [336, 794], [296, 761]], [[248, 726], [233, 681], [212, 644]]), R);
  d.step('桅杆与双帆', '竖直桅杆先定位，左右船帆一大一小。');
  d.line([[504, 660], [504, 218]], K, 12);
  d.shape([[478, 252], [478, 614], [232, 614]], Y);
  d.shape(curve([532, 306], [[672, 388], [716, 490], [760, 602]], [[666, 584], [606, 596], [532, 602]]), B);
  d.step('旗子与舷窗', '小旗指向一边，白色圆窗沿船身排列。');
  d.shape([[506, 217], [612, 239], [506, 283]], R);
  [370, 506, 639].forEach(x => d.ring(x, 720, 20, 20, W, 9));
  d.line([[456, 330], [298, 588]], W, 9);
  d.step('太阳、波浪和海鸥', '太阳挂在右上，波浪用重复的小弧线，海鸥像展开的字母。');
  d.shape(ellipse(838, 148, 54, 54), Y);
  [822, 874].forEach((y, row) => d.line(curve([170 + row * 48, y], [[252, y - 38], [277, y + 35], [350, y]], [[418, y - 37], [446, y + 35], [515, y]], [[587, y - 38], [619, y + 35], [688, y]], [[745, y - 30], [783, y + 22], [837 - row * 38, y]]), B, 11));
  d.line(curve([692, 262], [[713, 242], [741, 247], [749, 269]], [[765, 247], [790, 250], [808, 269]]), K, 7);
  d.line(curve([238, 330], [[259, 310], [287, 315], [295, 337]], [[311, 315], [336, 318], [354, 337]]), K, 7);
  return d.done('boat', '帆船', '三角构图 · 波浪', ['船', '大海', '海边', '旅行', '日落']);
}
function balloon(level: LessonLevel) {
  if (level !== 0) return independentLesson('balloon', level);
  const d = new Lesson('balloon', level);
  const outer = curve([416, 636], [[407, 565], [257, 472], [262, 332]], [[260, 103], [736, 103], [738, 332]], [[743, 465], [596, 567], [584, 636]], [[540, 652], [461, 652], [416, 636]]);
  d.step('圆顶和收口', '顶部圆圆的，向下慢慢收窄，留出吊篮位置。'); d.shape(outer, Y);
  d.step('弧形色带', '色带跟着气球弯曲，别画成笔直的条纹。');
  d.shape(curve([501, 163], [[346, 197], [356, 444], [456, 642]], [[475, 646], [485, 647], [501, 647]], [[441, 420], [443, 265], [501, 163]]), R, R);
  d.shape(curve([533, 166], [[710, 230], [668, 455], [552, 642]], [[566, 641], [576, 639], [584, 636]], [[720, 455], [738, 235], [533, 166]]), B, B);
  d.line(outer, K, 9);
  d.step('挂上藤编篮', '四根绳连到篮子，再用交叉线画编织纹。');
  [[424, 650, 447, 752], [462, 655, 472, 752], [540, 655, 530, 752], [578, 650, 555, 752]].forEach(([x, y, a, b]) => d.line([[x, y], [a, b]], K, 7));
  d.shape([[424, 749], [577, 749], [562, 850], [440, 850]], Y);
  [465, 497, 529].forEach(x => d.line([[x, 768], [x + 12, 834]], R, 5)); [783, 811].forEach(y => d.line([[443, y], [559, y]], R, 5));
  d.step('白云衬高度', '云放在两侧，主角周围留白才轻盈。');
  const cloud = (x: number, y: number) => d.line(curve([x, y], [[x - 38, y - 27], [x - 11, y - 65], [x + 21, y - 52]], [[x + 24, y - 117], [x + 109, y - 112], [x + 115, y - 51]], [[x + 169, y - 65], [x + 181, y + 5], [x + 137, y + 8]], [[x + 78, y + 9], [x + 38, y + 9], [x, y]]), B, 7);
  cloud(135, 624); cloud(725, 575);
  return d.done('balloon', '热气球', '曲面色带 · 留白', ['气球', '天空', '旅行', '风景', '云']);
}
function rocket(level: LessonLevel) {
  if (level !== 0) return independentLesson('rocket', level);
  const d = new Lesson('rocket', level);
  d.step('火焰和尾翼', '先画后面的火焰、尾翼，机身随后盖住连接处。');
  d.shape(curve([432, 652], [[362, 790], [470, 878], [500, 927]], [[551, 868], [641, 782], [568, 652]]), Y, Y);
  d.shape([[468, 693], [499, 851], [533, 693]], R, R);
  d.shape([[395, 493], [302, 616], [302, 745], [435, 655]], R); d.shape([[605, 493], [698, 616], [698, 745], [565, 655]], R);
  d.step('流线型机身', '上尖下宽，两边用对称的长弧线连起来。');
  const body = curve([500, 144], [[361, 252], [375, 486], [404, 666]], [[459, 688], [541, 688], [596, 666]], [[626, 486], [639, 252], [500, 144]]);
  d.shape(body, W);
  d.shape(curve([500, 144], [[447, 187], [415, 250], [404, 309]], [[460, 330], [542, 330], [596, 309]], [[584, 246], [553, 187], [500, 144]]), R);
  d.step('圆窗与机身条纹', '大圆套小圆，留一道白弧当作玻璃反光。');
  d.shape(ellipse(500, 442, 78, 78), Y); d.shape(ellipse(500, 442, 57, 57), B);
  d.line(curve([465, 438], [[462, 414], [480, 400], [499, 403]]), W, 10);
  d.line([[410, 621], [591, 621]], B, 20);
  d.step('星星和速度线', '星星大小错开，尾部短线表现向上飞。');
  [[217, 290, 31], [780, 400, 38], [732, 184, 24], [228, 498, 19]].forEach(([x, y, r]) => d.star(x, y, r));
  d.line([[342, 790], [328, 844]], B, 7).line([[659, 788], [672, 845]], B, 7);
  return d.done('rocket', '小火箭', '前后遮挡 · 动感', ['火箭', '太空', '星空', '宇宙', '科技']);
}
function fish(level: LessonLevel) {
  if (level !== 0) return independentLesson('fish', level);
  const d = new Lesson('fish', level);
  d.step('展开扇形尾巴', '尾巴像两片小扇子，先画在身体后面。');
  const tail = curve([646, 482], [[722, 360], [822, 326], [849, 365]], [[788, 446], [790, 566], [849, 655]], [[773, 679], [694, 612], [646, 553]]);
  d.shape(tail, R); d.line([[693, 500], [810, 389]], Y, 9).line([[702, 530], [812, 628]], Y, 9);
  d.shape(curve([386, 380], [[411, 272], [554, 275], [593, 380]]), R);
  d.step('椭圆鱼身', '头部圆，尾部窄，让身体接住刚才的尾巴。');
  d.shape(ellipse(469, 506, 221, 159), Y);
  d.step('眼睛和鱼鳍', '眼睛靠前，鱼鳍像叶片，弧线表示鱼鳃。');
  d.shape(ellipse(333, 464, 39, 43), W); d.dot(323, 465, 24).dot(318, 458, 7, W);
  d.line(curve([402, 425], [[436, 464], [436, 532], [405, 570]]), R, 9);
  d.shape(curve([479, 537], [[487, 599], [551, 645], [576, 618]], [[558, 578], [536, 556], [479, 537]]), R);
  d.line(curve([261, 531], [[281, 551], [306, 549], [316, 536]]), K, 6);
  d.step('鳞片水草和气泡', '鳞片只画几片，空白留给气泡和摇摆的水草。');
  [491, 553, 610].forEach(x => d.line(ellipse(x, 465, 19, 28, -1.1, 1.1), R, 6));
  [[210, 382, 24], [261, 284, 34], [200, 196, 18]].forEach(([x, y, r]) => d.ring(x, y, r, r, B, 7));
  [245, 719, 794].forEach((x, i) => d.line(curve([x, 842], [[x - 49, 776], [x + 40, 770], [x + (i % 2 ? 13 : -13), 702]]), G, 17));
  d.line([[188, 855], [832, 855]], B, 7);
  return d.done('fish', '小金鱼', '扇形尾巴 · 装饰', ['金鱼', '鱼', '动物', '水族', '池塘']);
}

const ORIGINAL_EXAMPLES = [
  { id: 'cat', title: '小猫', skill: '圆形组合 · 表情', keywords: ['猫', '貓', '动物', '宠物', '可爱', '脸部'], create: cat },
  { id: 'panda', title: '熊猫', skill: '黑白关系 · 遮挡', keywords: ['大熊猫', '熊貓', '动物', '竹子', '黑白', '国宝'], create: panda },
  { id: 'sunflower', title: '向日葵', skill: '重复花瓣 · 排列', keywords: ['花', '植物', '花朵', '黄色', '盆栽'], create: sunflower },
  { id: 'strawberry', title: '草莓', skill: '水滴轮廓 · 高光', keywords: ['水果', '食物', '红色', '甜点', '果实'], create: strawberry },
  { id: 'icecream', title: '冰淇淋', skill: '云朵曲线 · 格纹', keywords: ['冰激凌', '食物', '甜点', '奶油', '夏天'], create: icecream },
  { id: 'house', title: '小房子', skill: '几何组合 · 远近', keywords: ['房子', '建筑', '家', '风景', '乡村'], create: house },
  { id: 'boat', title: '帆船', skill: '三角构图 · 波浪', keywords: ['船', '交通', '大海', '海边', '旅行', '日落'], create: boat },
  { id: 'balloon', title: '热气球', skill: '曲面色带 · 留白', keywords: ['气球', '交通', '天空', '旅行', '风景', '云'], create: balloon },
  { id: 'rocket', title: '小火箭', skill: '前后遮挡 · 动感', keywords: ['火箭', '交通', '太空', '星空', '宇宙', '科技'], create: rocket },
  { id: 'fish', title: '小金鱼', skill: '扇形尾巴 · 装饰', keywords: ['金鱼', '鱼', '动物', '水族', '池塘'], create: fish },
  ...ANIMAL_EXAMPLES,
  ...NATURE_EXAMPLES,
  ...OBJECT_EXAMPLES,
  ...LIGHTHOUSE_EXAMPLES,
  ...WILDLIFE_EXAMPLES,
  ...BOTANICAL_EXAMPLES,
  ...SCENE_EXAMPLES,
];

export const DRAWING_EXAMPLES: DrawingExample[] = [
  ...ORIGINAL_EXAMPLES.map(item => ({ ...item, wordIds: ORIGINAL_EXAMPLE_WORD_IDS[item.id] ?? [] })),
  ...SKY_EXAMPLES,
  ...FRUIT_EXAMPLES,
  ...MEADOW_EXAMPLES,
  ...STILL_LIFE_EXAMPLES,
  ...SCIENCE_EXAMPLES,
  ...PEOPLE_EXAMPLES,
  ...DAILY_LIFE_EXAMPLES,
  ...WEATHER_EXAMPLES,
  ...MUSIC_EXAMPLES,
  ...ACTION_EXAMPLES,
  ...FABLE_EXAMPLES,
  ...OUTING_EXAMPLES,
  ...GAME_PROP_EXAMPLES,
  ...SPORTS_EXAMPLES,
  ...FESTIVAL_EXAMPLES,
  ...IDIOM_EXAMPLES,
];
